import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';

const alertVariants = cva('relative rounded-lg border p-4', {
  variants: {
    variant: {
      info: 'bg-info/10 border-info/20 text-info',
      warning: 'bg-warning/10 border-warning/20 text-warning',
      success: 'bg-success/10 border-success/20 text-success',
      error: 'bg-destructive/10 border-destructive/20 text-destructive',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string;
  icon?: boolean;
}

type AlertDescriptionProps = React.HTMLAttributes<HTMLDivElement>;

type AlertTitleProps = React.HTMLAttributes<HTMLHeadingElement>;

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, title, icon = true, children, ...props }, ref) => {
    const getIcon = () => {
      if (!icon) return null;

      const iconClassName = 'h-4 w-4 mr-2 flex-shrink-0';

      switch (variant) {
        case 'success':
          return <CheckCircleIcon className={iconClassName} />;
        case 'warning':
          return <ExclamationTriangleIcon className={iconClassName} />;
        case 'error':
          return <XCircleIcon className={iconClassName} />;
        default:
          return <InformationCircleIcon className={iconClassName} />;
      }
    };

    return (
      <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        <div className="flex">
          {getIcon()}
          <div className="flex-1">
            {title && (
              <AlertTitle className="mb-1 font-medium leading-none tracking-tight">
                {title}
              </AlertTitle>
            )}
            <AlertDescription>{children}</AlertDescription>
          </div>
        </div>
      </div>
    );
  }
);
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, AlertTitleProps>(
  ({ className, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('mb-1 font-medium leading-none tracking-tight', className)}
      {...props}
    />
  )
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLParagraphElement, AlertDescriptionProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
  )
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
